import React, { useState, useEffect } from 'react';
import { woundAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Camera, X, ChevronLeft, ChevronRight, ZoomIn, GitCompareArrows, Check, ArrowRight, TrendingDown, TrendingUp, Minus } from 'lucide-react';

export default function WoundPhotoGallery({ woundId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await woundAPI.photos(woundId);
        setPhotos(res.data);
      } catch { /* no photos */ }
      finally { setLoading(false); }
    };
    load();
  }, [woundId]);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const navigateLightbox = (dir) => {
    if (lightbox === null) return;
    const next = lightbox + dir;
    if (next >= 0 && next < photos.length) setLightbox(next);
  };

  const toggleSelect = (idx) => {
    if (!compareMode) return;
    if (selected.includes(idx)) {
      setSelected(selected.filter(i => i !== idx));
    } else if (selected.length < 2) {
      setSelected([...selected, idx]);
    }
  };

  const exitCompare = () => {
    setCompareMode(false);
    setSelected([]);
  };

  if (loading) return <div className="h-32 bg-slate-100 rounded-2xl animate-pulse" />;

  if (photos.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400" data-testid="photo-gallery-empty">
        <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Keine Wundfotos vorhanden</p>
        <p className="text-xs mt-1">Fotos werden beim Erstellen eines Berichts hochgeladen</p>
      </div>
    );
  }

  // Comparison View
  if (compareMode && selected.length === 2) {
    const [aIdx, bIdx] = selected.sort((a, b) => a - b);
    const before = photos[aIdx];
    const after = photos[bIdx];
    const areaDiff = after.flaeche - before.flaeche;
    const areaPct = before.flaeche > 0 ? Math.round((areaDiff / before.flaeche) * 100) : 0;
    const painDiff = after.schmerz_nrs - before.schmerz_nrs;
    const AreaIcon = areaDiff < 0 ? TrendingDown : areaDiff > 0 ? TrendingUp : Minus;
    const PainIcon = painDiff < 0 ? TrendingDown : painDiff > 0 ? TrendingUp : Minus;
    const areaColor = areaDiff < 0 ? 'text-emerald-600' : areaDiff > 0 ? 'text-rose-600' : 'text-slate-500';
    const painColor = painDiff < 0 ? 'text-emerald-600' : painDiff > 0 ? 'text-rose-600' : 'text-slate-500';

    return (
      <div data-testid="photo-compare-view" className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-heading font-bold text-slate-900">Vorher / Nachher Vergleich</h3>
          <Button variant="outline" size="sm" onClick={exitCompare} className="h-8 text-xs rounded-lg" data-testid="exit-compare-btn">
            <X className="w-3 h-3 mr-1" />Vergleich beenden
          </Button>
        </div>

        {/* Side-by-side photos */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Badge className="bg-slate-700 text-white text-[10px] px-2 py-0.5">Vorher</Badge>
            <div className="aspect-square rounded-xl overflow-hidden border-2 border-slate-300 bg-slate-100">
              <img src={before.foto} alt="Vorher" className="w-full h-full object-cover" />
            </div>
            <div className="text-center">
              <p className="text-xs font-medium text-slate-700">{formatDate(before.datum)}</p>
              <p className="text-[10px] text-slate-500">{before.flaeche} cm² &middot; NRS {before.schmerz_nrs}/10</p>
              {before.wundphase && <Badge variant="outline" className="text-[9px] mt-0.5 border-slate-200">{before.wundphase}</Badge>}
            </div>
          </div>
          <div className="space-y-2">
            <Badge className="bg-teal-700 text-white text-[10px] px-2 py-0.5">Nachher</Badge>
            <div className="aspect-square rounded-xl overflow-hidden border-2 border-teal-300 bg-slate-100">
              <img src={after.foto} alt="Nachher" className="w-full h-full object-cover" />
            </div>
            <div className="text-center">
              <p className="text-xs font-medium text-slate-700">{formatDate(after.datum)}</p>
              <p className="text-[10px] text-slate-500">{after.flaeche} cm² &middot; NRS {after.schmerz_nrs}/10</p>
              {after.wundphase && <Badge variant="outline" className="text-[9px] mt-0.5 border-teal-200">{after.wundphase}</Badge>}
            </div>
          </div>
        </div>

        {/* Difference Display */}
        <Card className="border border-slate-200 shadow-sm" data-testid="compare-diff-card">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Veränderung</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-xl bg-slate-50">
                <p className="text-[10px] text-slate-500 mb-1">Fläche</p>
                <div className="flex items-center justify-center gap-1">
                  <AreaIcon className={`w-4 h-4 ${areaColor}`} />
                  <span className={`text-lg font-bold ${areaColor}`} data-testid="compare-area-diff">
                    {areaDiff > 0 ? '+' : ''}{areaDiff} cm²
                  </span>
                </div>
                <p className={`text-xs mt-0.5 ${areaColor}`}>
                  {areaPct > 0 ? '+' : ''}{areaPct}%
                </p>
                <p className="text-[10px] text-slate-400 mt-1">{before.flaeche} <ArrowRight className="w-3 h-3 inline" /> {after.flaeche} cm²</p>
              </div>
              <div className="text-center p-3 rounded-xl bg-slate-50">
                <p className="text-[10px] text-slate-500 mb-1">Schmerz (NRS)</p>
                <div className="flex items-center justify-center gap-1">
                  <PainIcon className={`w-4 h-4 ${painColor}`} />
                  <span className={`text-lg font-bold ${painColor}`} data-testid="compare-pain-diff">
                    {painDiff > 0 ? '+' : ''}{painDiff}
                  </span>
                </div>
                <p className={`text-xs mt-0.5 ${painColor}`}>
                  {painDiff < 0 ? 'Verbessert' : painDiff > 0 ? 'Verschlechtert' : 'Unverändert'}
                </p>
                <p className="text-[10px] text-slate-400 mt-1">{before.schmerz_nrs} <ArrowRight className="w-3 h-3 inline" /> {after.schmerz_nrs} /10</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Choose different photos */}
        <Button variant="outline" onClick={() => setSelected([])} className="w-full h-10 rounded-xl text-sm" data-testid="reselect-photos-btn">
          <GitCompareArrows className="w-4 h-4 mr-2" />Andere Fotos auswählen
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="wound-photo-gallery">
      {/* Compare Mode Toggle */}
      {photos.length >= 2 && (
        <div className="mb-3">
          {compareMode ? (
            <div className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-xl">
              <p className="text-xs text-teal-800 font-medium">
                {selected.length === 0 && 'Wählen Sie 2 Fotos zum Vergleichen'}
                {selected.length === 1 && 'Noch 1 Foto auswählen'}
              </p>
              <Button variant="ghost" size="sm" onClick={exitCompare} className="h-7 text-xs text-teal-700" data-testid="cancel-compare-btn">
                Abbrechen
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setCompareMode(true)} className="w-full h-10 rounded-xl border-teal-200 text-teal-700 hover:bg-teal-50 text-sm" data-testid="start-compare-btn">
              <GitCompareArrows className="w-4 h-4 mr-2" />Vorher/Nachher vergleichen
            </Button>
          )}
        </div>
      )}

      {/* Photo Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {photos.map((p, idx) => {
          const isSelected = selected.includes(idx);
          return (
            <Card
              key={p.report_id}
              className={`border shadow-sm overflow-hidden cursor-pointer transition-all group ${
                compareMode
                  ? isSelected
                    ? 'border-teal-500 ring-2 ring-teal-200 shadow-md'
                    : selected.length < 2
                      ? 'border-slate-200 hover:border-teal-300'
                      : 'border-slate-200 opacity-50'
                  : 'border-slate-200 hover:shadow-md'
              }`}
              onClick={() => compareMode ? toggleSelect(idx) : setLightbox(idx)}
              data-testid={`photo-card-${idx}`}
            >
              <div className="aspect-square relative overflow-hidden bg-slate-100">
                <img src={p.foto} alt={`Wundfoto ${formatDate(p.datum)}`} className="w-full h-full object-cover" loading="lazy" />
                {compareMode && isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center" data-testid={`photo-selected-${idx}`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
                {!compareMode && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                    <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </div>
              <CardContent className="p-2">
                <p className="text-xs font-medium text-slate-700">{formatDate(p.datum)}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                  <span>{p.flaeche} cm²</span>
                  <span>NRS {p.schmerz_nrs}</span>
                  {p.wundphase && <Badge variant="outline" className="text-[9px] border-slate-200 py-0 px-1">{p.wundphase}</Badge>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" data-testid="photo-lightbox" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 p-2 text-white/80 hover:text-white z-10" onClick={() => setLightbox(null)} data-testid="lightbox-close">
            <X className="w-6 h-6" />
          </button>
          {lightbox > 0 && (
            <button className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white z-10" onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}>
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {lightbox < photos.length - 1 && (
            <button className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white z-10" onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}>
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          <div className="max-w-4xl max-h-[80vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img src={photos[lightbox].foto} alt="Wundfoto" className="max-w-full max-h-[70vh] object-contain rounded-lg" />
            <div className="text-center mt-3 text-white">
              <p className="text-sm font-medium">{formatDate(photos[lightbox].datum)}</p>
              <p className="text-xs text-white/70 mt-0.5">
                Fläche: {photos[lightbox].flaeche} cm² &middot; NRS: {photos[lightbox].schmerz_nrs}/10
                {photos[lightbox].wundphase && ` · ${photos[lightbox].wundphase}`}
              </p>
              <p className="text-xs text-white/50 mt-1">{lightbox + 1} / {photos.length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
